// Pure filter pipeline for the command bar. Lives separately from
// the React palette so we can unit-test it without rendering.
//
// Two stages:
//
//   1. eligible(cmd, ctx) — synchronous predicate covering scope +
//      permission + feature gates. Used both by the palette filter
//      and the global-hotkey binder.
//   2. matchByQuery(cmds, query) — light fuzzy match by label /
//      keywords. cmdk does its own sorting inside the modal; we
//      use this for the right-click menus + the cheatsheet page
//      where cmdk isn't mounted.
import type { Command, CommandContext, CommandScope } from "@/lib/commands/types";

export function scopeAvailable(scope: CommandScope, ctx: CommandContext): boolean {
  switch (scope) {
    case "global":
      return true;
    case "load_detail":
      return Boolean(ctx.loadId);
    case "driver_detail":
      return Boolean(ctx.driverId);
    case "customer_detail":
      return Boolean(ctx.customerId);
    case "fulfillment_panel":
      return Boolean(ctx.fulfillmentRequestId);
    default:
      // Exhaustiveness check at compile time: TS will yell if we
      // add a CommandScope variant without handling it here.
      return false;
  }
}

export function eligible(cmd: Command, ctx: CommandContext): boolean {
  if (!scopeAvailable(cmd.scope, ctx)) return false;
  if (cmd.permission && !ctx.has(cmd.permission)) return false;
  if (cmd.featureFlag && !ctx.hasFeature(cmd.featureFlag)) return false;
  return true;
}

function tokenScore(haystack: string, needle: string): number {
  if (!needle) return 1;
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  if (h === n) return 100;
  if (h.startsWith(n)) return 60;
  if (h.includes(n)) return 30;
  // Subsequence match: every char in needle appears in order.
  let i = 0;
  for (const ch of h) {
    if (ch === n[i]) i++;
    if (i === n.length) return 10;
  }
  return 0;
}

export function matchByQuery(
  commands: Command[],
  query: string,
): Command[] {
  const q = query.trim();
  if (!q) return commands;
  return commands
    .map((cmd) => {
      const haystacks = [cmd.label, ...(cmd.keywords ?? [])];
      const best = Math.max(...haystacks.map((h) => tokenScore(h, q)));
      return { cmd, score: best };
    })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.cmd);
}

// filterCommands does both stages in order. The palette uses cmdk's
// own scoring while open, so the React component calls just
// `eligible` per command; consumers that want a full pipeline (the
// keyboard-shortcuts page, the right-click menu) call this.
export function filterCommands(
  commands: Command[],
  ctx: CommandContext,
  query: string,
): Command[] {
  const eligibles = commands.filter((c) => eligible(c, ctx));
  return matchByQuery(eligibles, query);
}
